import React, { useEffect, useState } from "react";
import {
  XMarkIcon,
  PlusIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";
import SearchableDropdown from "./SearchableDropdown";
import TableHeaderFilter from "./TableHeaderfilter";
import ExcelExport from "./ExcelExport";

const inputStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-100";

/* ── status badge ────────────────────────────────────────────────────────────── */
const getStatusStyle = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("open") || s.includes("scheduled")) return "bg-amber-50 text-amber-700 border border-amber-200";
  else if (s.includes("in progress")) return "bg-blue-50 text-blue-700 border border-blue-200";
  else if (s.includes("closed")) return "bg-green-50 text-green-700 border border-green-200";
  else if (s.includes("lost")) return "bg-rose-50 text-rose-700 border border-rose-200";
  else if (s.includes("successful (payment not received)")) return "bg-yellow-50 text-yellow-700 border border-yellow-200";
  else if (s.includes("successful & payment received")) return "bg-emerald-50 text-emerald-700 border border-emerald-200";
  else return "bg-slate-50 text-slate-600 border border-slate-200";
};

/* ════════════════════════════════════════════════════════════════════════════ */
const FollowUpModal = ({ isOpen, onClose }) => {

  const employee = JSON.parse(localStorage.getItem("employee")) || {
    employee_name: "",
    employee_id: "",
  };

  const [view, setView] = useState("list");
  const [isNewRoot, setIsNewRoot] = useState(false);
  const [latestFollowUps, setLatestFollowUps] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [selectedFollowUpId, setSelectedFollowUpId] = useState(null);
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [filteredLatest, setFilteredLatest] = useState([]);
  const [filters, setFilters] = useState({});
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [totalCount, setTotalCount] = useState(0);

  const entityOptions = [
    { label: "Lead", value: "Lead" },
    { label: "Customer", value: "Customer" },
  ];

  const followUpTypeOptions = [
    { label: "Call", value: "Call" },
    { label: "Visit", value: "Visit" },
    { label: "Meeting", value: "Meeting" },
    { label: "Email", value: "Email" },
  ];

  const statusOptions = [
    { label: "Scheduled", value: "Open" },
    { label: "In Progress", value: "In Progress" },
    { label: "Closed", value: "Closed" },
    { label: "Lost", value: "Lost"},
    { label: "Successful (Payment Not Received)", value: "Successful (Payment Not Received)" },
    { label: "Successful & payment received", value: "Successful & payment received" },
  ];

  const emptyForm = {
    product_name: "", customer_name: "", entityType: "", nextFollowUp: "",
    followUpType: "", purpose: "", assignedTo: "", status: "", outcome: "",
  };

  const [form, setForm] = useState(emptyForm);

  const getColumnOptions = (key) => {
    if (!latestFollowUps?.length) return [];
    const unique = [...new Set(latestFollowUps.map((item) => item?.[key]).filter((v) => v !== null && v !== undefined && v !== ""))];
    return unique.map((val) => ({ label: String(val), value: String(val) }));
  };

  const fetchLatest = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/followUpHistory.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: employee.employee_id }),
      });
      const data = await res.json();
      if (data.status) { setLatestFollowUps(data.data); setFilteredLatest(data.data); }
    } catch (err) { console.error("fetchLatest error:", err); }
  };

  const fetchTimeline = async (id) => {
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/followUpHistory.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ follow_up_id: id, userId: employee.employee_id }),
      });
      const data = await res.json();
      if (data.status) setTimeline(data.data);
    } catch (err) { console.error("fetchTimeline error:", err); }
  };

  const fetchProducts = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/DataFetch/productDetailsFetch.php`);
      const data = await res.json();
      if (data.status && Array.isArray(data.productList)) {
        setProductOptions(data.productList.map((p) => ({ label: p.product_name, value: p.product_name })));
      }
    } catch (err) { console.error("fetchProducts error:", err); }
  };

  const fetchCustomers = async (product) => {
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/getCustomers.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_name: product }),
      });
      const data = await res.json();
      if (data.status) setCustomerOptions(data.data.map((c) => ({ label: c.company_name, value: c.company_name })));
    } catch (err) { console.error("fetchCustomers error:", err); }
  };

  const fetchAssignedTo = async () => {
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/followUpHistory.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (data.status) setAssignedToOptions(data.employees);
    } catch (err) { console.error("fetchAssignedTo error:", err); }
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchLatest();
    fetchAssignedTo();
    setView("list");
    setForm(emptyForm);
    setTimeline([]);
    setSelectedFollowUpId(null);
  }, [isOpen]);

  useEffect(() => {
    let temp = [...latestFollowUps];
    Object.keys(filters).forEach((key) => {
      const selected = filters[key];
      if (selected && selected.length > 0) temp = temp.filter((row) => selected.includes(String(row?.[key])));
    });
    if (selectedMonth) {
      const [year, month] = selectedMonth.split("-");
      temp = temp.filter((row) => {
        if (!row.follow_up_date_time) return false;
        const d = new Date(row.follow_up_date_time);
        return d.getFullYear() === Number(year) && d.getMonth() + 1 === Number(month);
      });
    }
    setFilteredLatest(temp);
    setTotalCount(temp.length);
  }, [filters, latestFollowUps, selectedMonth]);

  const handleChange = (e) => { setForm({ ...form, [e.target.name]: e.target.value }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const basePayload = {
        employee_name: employee.employee_name,
        employee_id: employee.employee_id,
        product_name: form.product_name,
        customer_name: form.customer_name,
        entity_type: form.entityType,
        follow_up_type: form.followUpType,
        next_follow_up: form.nextFollowUp ? form.nextFollowUp.replace("T", " ") : null,
        purpose: form.purpose,
        assigned_to: form.assignedTo,
        follow_up_status: form.status,
        outcome: form.outcome,
      };
      const url = isNewRoot ? `${SERVER_URL}/Rahul/insertNewFollowUp.php` : `${SERVER_URL}/Rahul/updateFollowUp.php`;
      const payload = isNewRoot ? basePayload : { ...basePayload, follow_up_id: selectedFollowUpId };
      const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!data.status) { alert(data.message || "Operation failed"); return; }
      setForm(emptyForm);
      if (isNewRoot) { fetchLatest(); setView("list"); }
      else { fetchTimeline(selectedFollowUpId); setView("timeline"); }
    } catch (err) { console.error("submit error:", err); }
  };

  if (!isOpen) return null;

  const latestColumns = [
    { header: "FollowUp ID", key: "follow_up_id" },
    { header: "Date", key: "follow_up_date_time" },
    { header: "Employee", key: "employee_name" },
    { header: "Product", key: "product_name" },
    { header: "Customer", key: "customer_name" },
    { header: "Entity", key: "entity_type" },
    { header: "Type", key: "follow_up_type" },
    { header: "Next Follow Up", key: "next_follow_up" },
    { header: "Purpose", key: "purpose" },
    { header: "Status", key: "follow_up_status" },
  ];

  const timelineColumns = [
    { header: "Date", key: "follow_up_date_time" },
    { header: "Product", key: "product_name" },
    { header: "Customer", key: "customer_name" },
    { header: "Type", key: "follow_up_type" },
    { header: "Purpose", key: "purpose" },
    { header: "Assigned To", key: "assigned_to" },
    { header: "Outcome", key: "outcome" },
    { header: "Next Follow Up", key: "next_follow_up" },
    { header: "Status", key: "follow_up_status" },
  ];

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="fixed inset-0 z-50 flex items-center justify-center p-2"
        style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}>
        <div
          className="w-full max-w-5xl rounded-2xl flex flex-col max-h-[90vh] bg-white border border-slate-200"
          style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
        >

          {/* HEADER */}
          <div
            className="px-6 py-4 flex justify-between items-center border-b border-slate-200 rounded-t-2xl"
            style={{ background: "#f8fafc" }}
          >
            <div className="flex items-center gap-3">
              {view !== "list" && (
                <button
                  onClick={() => setView("list")}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 transition-colors"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </button>
              )}
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full"
                  style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                <h2 className="text-lg font-bold text-slate-800">
                  {view === "list" && "Follow-up History"}
                  {view === "timeline" && "Follow-up Timeline"}
                  {view === "form" && (isNewRoot ? "New Follow-up" : "Add Follow-up")}
                </h2>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* BODY */}
          <div className="flex-1 overflow-y-auto p-5 att-scrollbar">

            {/* ── LIST VIEW ── */}
            {view === "list" && (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 rounded-xl border border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => { setIsNewRoot(true); setForm(emptyForm); fetchProducts(); setCustomerOptions([]); setView("form"); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                      style={{
                        background: "linear-gradient(135deg, #1e40af, #0369a1)",
                        boxShadow: "0 2px 8px rgba(30,64,175,0.18)",
                      }}
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Follow-up
                    </button>
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Select Month</label>
                      <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div className="text-xs font-bold text-slate-600 bg-white px-3 py-1.5 rounded-lg border border-slate-200">
                      Total: <span className="text-slate-800">{totalCount}</span>
                    </div>
                    <ExcelExport data={filteredLatest} columns={latestColumns} fileName="FollowUpHistory.xlsx" reportTitle="Follow Up History" />
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[55vh] overflow-y-auto rounded-xl border border-slate-200 att-scrollbar">
                  <table className="min-w-[900px] w-full text-sm border-collapse">
                    <thead className="sticky top-0 z-20" style={{ background: "#f8fafc" }}>
                      <tr>
                        {latestColumns.map((col) => (
                          <th key={col.key} className="p-3 text-left border-b border-slate-200">
                            {["follow_up_date_time", "purpose"].includes(col.key) ? (
                              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{col.header}</span>
                            ) : (
                              <TableHeaderFilter
                                label={col.header}
                                options={getColumnOptions(col.key)}
                                selectedValues={filters[col.key] || []}
                                onChange={(vals) => setFilters((prev) => ({ ...prev, [col.key]: vals }))}
                              />
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLatest.map((fu) => (
                        <tr
                          key={fu.follow_up_id}
                          className="border-t border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/60"
                          onClick={() => {
                            setSelectedFollowUpId(fu.follow_up_id);
                            setForm({ ...emptyForm, product_name: fu.product_name, customer_name: fu.customer_name, entityType: fu.entity_type });
                            fetchTimeline(fu.follow_up_id);
                            setView("timeline");
                          }}
                        >
                          {latestColumns.map((col) => (
                            <td key={col.key} className="p-3 text-slate-500 text-xs">
                              {col.key === "follow_up_status" ? (
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusStyle(fu[col.key])}`}>
                                  {fu[col.key]}
                                </span>
                              ) : fu[col.key]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ── TIMELINE VIEW ── */}
            {view === "timeline" && (
              <>
                <div className="flex justify-between items-center mb-4">
                  <button
                    onClick={() => { setIsNewRoot(false); setView("form"); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                    style={{
                      background: "linear-gradient(135deg, #1e40af, #0369a1)",
                      boxShadow: "0 2px 8px rgba(30,64,175,0.18)",
                    }}
                  >
                    <PlusIcon className="w-4 h-4" />
                    ADD
                  </button>
                  <ExcelExport data={timeline} columns={timelineColumns} fileName="FollowUpTimeline.xlsx" reportTitle="Follow Up Timeline" />
                </div>

                <div className="overflow-x-auto max-h-[55vh] overflow-y-auto rounded-xl border border-slate-200 att-scrollbar">
                  <table className="min-w-[800px] w-full text-sm border-collapse">
                    <thead className="sticky top-0 z-20" style={{ background: "#f8fafc" }}>
                      <tr>
                        {timelineColumns.map((col) => (
                          <th key={col.key} className="p-3 text-left border-b border-slate-200">
                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{col.header}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {timeline.map((t, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {timelineColumns.map((col) => (
                            <td key={col.key} className="p-3 text-slate-500 text-xs">
                              {col.key === "follow_up_status" ? (
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusStyle(t[col.key])}`}>
                                  {t[col.key]}
                                </span>
                              ) : t[col.key]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ── FORM VIEW ── */}
            {view === "form" && (
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Product */}
                {isNewRoot ? (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Product</label>
                    <SearchableDropdown
                      label=""
                      options={productOptions}
                      value={form.product_name}
                      onChange={(val) => { setForm((prev) => ({ ...prev, product_name: val, customer_name: "" })); fetchCustomers(val); }}
                      placeholder="Select Product"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Product</label>
                    <input value={form.product_name} disabled className={inputStyle} />
                  </div>
                )}

                {/* Customer */}
                {isNewRoot ? (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Customer</label>
                    <SearchableDropdown
                      label=""
                      options={customerOptions}
                      value={form.customer_name}
                      onChange={(val) => setForm({ ...form, customer_name: val })}
                      placeholder={form.product_name ? "Select customer" : "Select product first"}
                      disabled={!form.product_name}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Customer</label>
                    <input value={form.customer_name} disabled className={inputStyle} />
                  </div>
                )}

                {/* Entity */}
                {isNewRoot ? (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Entity</label>
                    <SearchableDropdown label="" options={entityOptions} value={form.entityType} onChange={(val) => setForm({ ...form, entityType: val })} placeholder="Select Entity" />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Entity</label>
                    <input value={form.entityType} disabled className={inputStyle} />
                  </div>
                )}

                {/* Next Follow Up */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Next Follow Up</label>
                  <input type="datetime-local" name="nextFollowUp" value={form.nextFollowUp} onChange={handleChange} className={inputStyle} />
                </div>

                {/* Follow-up Type */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Follow-up Type</label>
                  <SearchableDropdown label="" options={followUpTypeOptions} value={form.followUpType} onChange={(val) => setForm({ ...form, followUpType: val })} />
                </div>

                {/* Purpose */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Purpose</label>
                  <input name="purpose" value={form.purpose} onChange={handleChange} placeholder="Purpose" className={inputStyle} />
                </div>

                {/* Assigned To */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Assigned To</label>
                  <SearchableDropdown label="" options={assignedToOptions} value={form.assignedTo} onChange={(val) => setForm({ ...form, assignedTo: val })} placeholder="Select Employee" />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Status</label>
                  <SearchableDropdown label="" options={statusOptions} value={form.status} onChange={(val) => setForm({ ...form, status: val })} />
                </div>

                {/* Outcome */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Outcome</label>
                  <input name="outcome" value={form.outcome} onChange={handleChange} placeholder="Outcome" className={inputStyle} />
                </div>

                {/* Actions */}
                <div className="col-span-full flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setView(isNewRoot ? "list" : "timeline")}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                    style={{
                      background: "linear-gradient(135deg, #1e40af, #0369a1)",
                      boxShadow: "0 2px 8px rgba(30,64,175,0.18)",
                    }}
                  >
                    Submit
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FollowUpModal;